import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { orderService, Order } from '../services/orderService';

const STEPS = ['pending', 'confirmed', 'processing', 'shipped', 'delivered'] as const;

const stepLabels: Record<string, string> = {
  pending:    'Order Placed',
  confirmed:  'Confirmed',
  processing: 'Processing',
  shipped:    'Shipped',
  delivered:  'Delivered',
};

const stepIcons: Record<string, string> = {
  pending:    '📋',
  confirmed:  '✅',
  processing: '⚙️',
  shipped:    '🚚',
  delivered:  '🎉',
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

const OrderTrackPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    orderService.getOrderById(id).then(o => {
      setOrder(o);
      setLoading(false);
    });
  }, [id]);

  if (!user) return (
    <div className="min-h-screen bg-[#FCFCFC] flex items-center justify-center">
      <div className="text-center">
        <div className="text-4xl mb-4">🔒</div>
        <h2 className="text-2xl font-bold mb-2">Please login</h2>
        <button onClick={() => navigate('/login')} className="bg-[#DAA520] text-white px-6 py-2 rounded mt-4">Login</button>
      </div>
    </div>
  );

  if (loading) return (
    <div className="min-h-screen bg-[#FCFCFC] flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#DAA520]" />
    </div>
  );

  if (!order) return (
    <div className="min-h-screen bg-[#FCFCFC] flex items-center justify-center">
      <div className="text-center">
        <div className="text-4xl mb-4">❌</div>
        <h2 className="text-2xl font-bold mb-2">Order not found</h2>
        <button onClick={() => navigate('/orders')} className="bg-[#DAA520] text-white px-6 py-2 rounded mt-4">My Orders</button>
      </div>
    </div>
  );

  const isCancelled = order.status === 'cancelled';
  const currentStep = isCancelled ? -1 : STEPS.indexOf(order.status as typeof STEPS[number]);

  return (
    <div className="min-h-screen bg-[#FCFCFC]">
      <div className="py-8 px-4 md:px-20">
        <div className="max-w-3xl mx-auto">
          <button onClick={() => navigate('/orders')} className="flex items-center text-gray-600 hover:text-[#DAA520] mb-6">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Orders
          </button>

          {/* Status card */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-6">
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Order #{order.orderNumber}</h1>
                <p className="text-gray-500 text-sm mt-1">Placed on {formatDate(order.createdAt)}</p>
              </div>
              <span className={`px-4 py-1.5 rounded-full text-sm font-semibold self-start ${
                isCancelled ? 'bg-red-100 text-red-700' :
                order.status === 'delivered' ? 'bg-green-100 text-green-700' :
                'bg-yellow-100 text-yellow-700'
              }`}>
                {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
              </span>
            </div>

            {isCancelled ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                <p className="text-red-600 font-semibold text-lg">❌ Order Cancelled</p>
              </div>
            ) : (
              <div className="relative">
                <div className="absolute top-6 left-6 right-6 h-1 bg-gray-200 rounded hidden md:block" />
                <div className="absolute top-6 left-6 h-1 bg-[#DAA520] rounded hidden md:block transition-all duration-500"
                  style={{ width: currentStep >= 0 ? `${(currentStep / (STEPS.length - 1)) * (100 - 12)}%` : '0%' }} />
                <div className="flex flex-col md:flex-row justify-between gap-4 md:gap-0">
                  {STEPS.map((step, idx) => {
                    const done = idx <= currentStep;
                    const active = idx === currentStep;
                    return (
                      <div key={step} className="flex md:flex-col items-center gap-3 md:gap-2 flex-1">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl z-10 border-2 transition-all ${
                          done ? 'bg-[#DAA520] border-[#DAA520]' : 'bg-white border-gray-300'
                        } ${active ? 'ring-4 ring-[#DAA520]/30' : ''}`}>
                          {stepIcons[step]}
                        </div>
                        <div className="md:text-center">
                          <p className={`text-sm font-semibold ${done ? 'text-[#DAA520]' : 'text-gray-400'}`}>
                            {stepLabels[step]}
                          </p>
                          {active && <p className="text-xs text-gray-500 mt-0.5">In progress</p>}
                          {done && idx < currentStep && <p className="text-xs text-green-600 mt-0.5">Done</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Details grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="font-semibold text-gray-800 mb-3">Shipping Address</h2>
              <p className="text-gray-600 text-sm leading-relaxed">{order.shippingAddress}</p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="font-semibold text-gray-800 mb-3">Payment</h2>
              <p className="text-gray-600 text-sm">{order.paymentMethod.toUpperCase()}</p>
              <p className={`text-sm font-medium mt-1 ${order.paymentStatus === 'completed' ? 'text-green-600' : 'text-yellow-600'}`}>
                {order.paymentStatus.charAt(0).toUpperCase() + order.paymentStatus.slice(1)}
              </p>
              <p className="text-xl font-bold text-[#DAA520] mt-2">₹{(order.totalAmount ?? 0).toFixed(0)}</p>
            </div>
          </div>

          {/* Items */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="font-semibold text-gray-800 mb-4">Items ({order.items.length})</h2>
            <div className="space-y-4">
              {order.items.map((item, idx) => (
                <div key={idx} className="flex gap-4 items-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                    {item.imageUrl
                      ? <img src={encodeURI(item.imageUrl)} alt={item.name} className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center text-2xl">👗</div>
                    }
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-800">{item.name}</p>
                    <p className="text-gray-500 text-sm">Qty: {item.quantity}{item.selectedSize ? ` · Size: ${item.selectedSize}` : ''}</p>
                  </div>
                  <p className="font-semibold text-gray-800">₹{((item.price ?? 0) * (item.quantity ?? 1)).toFixed(0)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderTrackPage;
